import {
  ClinicalNumericValue,
  LinkedNumericValue
} from 'bz-core-controls/controls/clinical/clinical-numeric';
import Joi from 'joi';
import _ from 'lodash';
import { action, observable, runInAction } from 'mobx';
import React from 'react';
import { EditorState, Entity, RichUtils } from 'tm2-draft-js';
import ReviewStore from './review-store';
import ClinicalNumericExtension from './validation-extensions/clinical-numeric';

class ValidationStore extends ReviewStore {
  @observable submitted = false;
  @observable rules = {};
  @observable errors: Record<string, boolean> = {};

  @observable
  onReferralPortal = false;

  blacklistBuilder = new BlackListBuilder();

  joi: any;

  constructor(controls) {
    super(controls);
    this.joi = Joi.extend(new ClinicalNumericExtension());
  }

  getEntityData(range) {
    const entityKey = range.getEntity();
    if (!(entityKey > 0)) {
      return null;
    }

    const entity = Entity.get(entityKey);
    if (!entity) {
      return null;
    }

    const entityType = entity.getType().substring(8) || '';
    const attributes = entity.getData();
    const type = (this.controls[entityType] || {}).type || 'any';

    return {
      attributes,
      type
    };
  }

  @action.bound
  onRegulate() {
    let { editor } = this;
    let rules = {};
    const validationBuilder = new ValidationBuilder();

    let filter = (c) => {
      const entityData = this.getEntityData(c);
      if (!entityData) {
        return false;
      }

      const { attributes, type } = this.getEntityData(c);

      if (this.onReferralPortal && attributes.hideonreferralportal) {
        return;
      }
      if (this.onConnect && attributes.hideonpatientportal) {
        return;
      }
      if (rules.hasOwnProperty(attributes.field)) {
        return;
      }

      if (!(attributes.field || '').trim().length > 0) {
        return false;
      }
      this.joi[type]();
      let r = rules[attributes.field] || this.joi[type]();

      r = validationBuilder.build(type, attributes, r);

      rules[attributes.field] = r;
      return false;
    };

    let content = editor.getCurrentContent();

    content.getBlockMap().forEach((block, blockKey) => {
      block.findEntityRanges(filter, () => {});
    });

    this.rules = _.isEmpty(rules) ? this.joi.object() : this.joi.object().keys(rules).unknown();
  }

  onValidateSync(field = ''): boolean {
    if (!this.submitted) {
      return true;
    }

    const { rules, data } = this;
    const { error = {} } = rules?.validate?.(data, { abortEarly: false }) || {};
    const { details = [] } = error;

    // Apply blacklist filtering, same as async version
    const blackList = this.blacklistBuilder.build(this);
    const filteredErrors = details.filter((detail) => !blackList.has(detail.path[0]));

    return filteredErrors.length === 0;
  }

  onValidate(field = ''): Promise<boolean> {
    if (!this.submitted) {
      return Promise.resolve(true);
    }
    let { editor, rules, data } = this;
    let content = editor.getCurrentContent();
    let selection = editor.getSelection();

    let filter = (c) => {
      let entityKey = c.getEntity();
      if (!(entityKey > 0)) {
        return false;
      }
      if (!field) {
        return true;
      }
      let entity = Entity.get(entityKey);
      if (!entity) {
        return false;
      }
      let attributes = entity.getData();
      if (!(attributes.field || '').trim().length > 0) {
        return false;
      }
      return attributes.field === field;
    };

    let callback = (blockKey, start, end) => {
      let highlight = selection.merge({
        anchorKey: blockKey,
        anchorOffset: start,
        focusKey: blockKey,
        focusOffset: end
      });

      editor = EditorState.forceSelection(editor, highlight);
      editor = RichUtils.toggleInlineStyle(editor, 'REFRESH');
    };

    return new Promise((resolve, reject) => {
      const { error = {} } = rules?.validate?.(data, { abortEarly: false }) || {};
      content.getBlockMap().forEach((block, blockKey) => {
        block.findEntityRanges(filter, callback.bind(this, blockKey));
      });

      const { details = [] } = error;
      const { errorMessageRegex } = ValidationStore;
      const blackList = this.blacklistBuilder.build(this);

      const errors = details
        .filter((detail) => {
          return !blackList.has(detail.path[0]);
        })
        .map((detail) => {
          /* The regex stuff is a hack for the fact that JOI
           *  converts all non whites space and alphanumberic characters
           *  with html character codes
           */
          const { label = '' } = detail.context;
          const message = detail.message.replace(errorMessageRegex, `"${label}"`);

          return { ...detail, message: message };
        })
        .reduce((errors, detail) => {
          errors[detail.path] = detail.message;

          return errors;
        }, {});

      this.editor = EditorState.moveSelectionToEnd(editor);

      runInAction(() => {
        this.errors = errors;
      });

      if (_.isEmpty(errors)) {
        resolve(true);
      } else {
        reject('Validation Failed');
      }
    });
  }
}

ValidationStore.errorMessageRegex = /^\"(.*)\"/;

class ValidationBuilder {
  builders = ['min', 'max', 'label', 'required', 'isEmail', 'addressConfig'];

  getAttribute(attributes, attribute) {
    if (!attributes.hasOwnProperty(attribute)) {
      return null;
    }

    const obj = attributes[attribute];
    if (obj === null || obj === false || obj.toString().trim() === '') {
      return null;
    }

    return obj;
  }

  min(type, attributes, oldRule) {
    const value = this.getAttribute(attributes, 'min');

    return value === null ? oldRule : oldRule.min(parseFloat(value));
  }

  max(type, attributes, oldRule) {
    const value = this.getAttribute(attributes, 'max');

    return value === null ? oldRule : oldRule.max(parseFloat(value));
  }

  regex(type, attributes, oldRule) {
    const value = this.getAttribute(attributes, 'regex');

    return value === null ? oldRule : oldRule.regex(value);
  }

  label(type, attributes, oldRule) {
    const value = this.getAttribute(attributes, 'label');

    return value === null ? oldRule : oldRule.label(value);
  }

  addressConfig(type, attributes, oldRule) {
    const value = this.getAttribute(attributes, 'addressConfig');
    const label = this.getAttribute(attributes, 'label')
      ? this.getAttribute(attributes, 'label') + ' '
      : '';

    if (value === null) {
      return oldRule;
    }

    const { requiredFields } = value;

    const requiredFactory = (required: boolean, label: string) =>
      required
        ? Joi.string().required().invalid(null).invalid('').label(label)
        : Joi.string().allow('');

    const newRule = Joi.object({
      addressLine1: requiredFactory(requiredFields.addressLine1, label + 'Address Line 1'),
      addressLine2: requiredFactory(requiredFields.addressLine2, label + 'Address Line 2'),
      addressLine3: requiredFactory(requiredFields.town, label + 'Town'),
      addressLine4: requiredFactory(requiredFields.county, label + 'County'),
      addressLine5: requiredFactory(requiredFields.country, label + 'Country'),
      postCode: requiredFactory(requiredFields.postcode, label + 'Postcode')
    }).label('Address');

    return newRule;
  }

  isEmail(type, attributes, oldRule) {
    const isEmail = this.getAttribute(attributes, 'isEmail');
    return isEmail === null ? oldRule : oldRule.email({ tlds: { allow: false } });
  }

  required(type, attributes, oldRule) {
    const value = this.getAttribute(attributes, 'required');

    if (value === null) {
      return oldRule.allow(null).allow('');
    }

    const newRule = oldRule
      .required()
      .invalid(null)
      .invalid('')
      .empty()
      .messages({
        'string.base': `"${attributes.label ?? attributes.field}}" is a required field`,
        'date.base': `"${attributes.label ?? attributes.field}" is a required field`,
        'boolean.base': `"${attributes.label ?? attributes.field}}" is a required field`
      });

    if (type === 'array' && !attributes.hasOwnProperty('min')) {
      return newRule.min(1);
    }
    return newRule;
  }

  build(type, attributes, oldRule) {
    return this.builders.reduce((newRule, builder) => {
      return this[builder](type, attributes, newRule);
    }, oldRule);
  }
}

class BlackListBuilder {
  constructor() {
    this.findEntity = this.findEntity.bind(this);
    this.isValidEntity = this.isValidEntity.bind(this);
  }

  findEntity(range) {
    const { key } = range;
    const { [key]: entity } = this.entityMap;

    return entity;
  }

  isValidEntity(entity) {
    return !!entity && !!entity.data;
  }

  getEntities(block) {
    const entities = block.entityRanges.map(this.findEntity).filter(this.isValidEntity);

    return entities;
  }

  isGroupOpen(operator, operatorValue, field) {
    if (operator === 'never') {
      return false;
    }

    const raw = this.dataAsMap.get(field) || null;
    const value = raw === null ? raw : raw.toString();

    switch (operator) {
      case '=':
        return value === operatorValue;
      case '!':
        return value !== null && value !== operatorValue;
      case '<':
        return parseFloat(value) < parseFloat(operatorValue);
      case '>':
        return parseFloat(value) > parseFloat(operatorValue);
      case '|':
        return true;
      case '^':
        return true;
      default:
        return false;
    }
  }

  addChildren(blocks, open) {
    if (!blocks) {
      return;
    }

    blocks.forEach((block) => {
      this.errorBlackList(block.blocks, open);
    });
  }

  add(blocks, open) {
    if (!blocks || open) {
      return;
    }

    const { blackList } = this;

    blocks.forEach((block) => {
      this.getEntities(block).forEach((entity) => {
        const { field } = entity.data;

        blackList.push(field);
      });
    });
  }

  errorBlackList(blocks, parentOpen) {
    blocks
      .filter((block) => {
        return block.type === 'group-container';
      })
      .forEach((block) => {
        const { blocks: childBlocks } = block;
        this.getEntities(block).forEach((entity) => {
          const { operator, operatorValue, field } = entity.data;

          const open = parentOpen && this.isGroupOpen(operator, operatorValue, field);

          this.addChildren(childBlocks, open);
          this.add(childBlocks, open);
        });
      });
  }

  build(store) {
    const { dataAsMap, rawTemplate } = store;
    const entityMap = rawTemplate.get('entityMap') || {};
    const blocks = rawTemplate.get('blocks') || [];

    this.blackList = [];
    this.dataAsMap = dataAsMap;
    this.entityMap = entityMap;

    this.errorBlackList(blocks, true);

    const blackList = new Set(this.blackList);

    this.clear();

    return blackList;
  }

  clear() {
    this.blackList = null;
    this.data = null;
    this.entityMap = null;
  }
}

export default ValidationStore;
